import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaSave } from "react-icons/fa";
import Select from "react-select"; // Import react-select
import "../common/style.css";
import "./project.css";
import {
  updateProject,
  createProject,
  getProjectById,
  getState,
} from "../http/api"; // Add getState
import SideNav from "../common/sidenav";
import TopNav from "../common/topnav";
import Loading from "../loading/loading";

export default function AddProjectForm({ setToast, mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  //const mode = id === "new" ? "add" : "edit";

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    localStorage.getItem("sb|sidebar-toggle") === "true"
  );

  const [formData, setFormData] = useState({
    name: "",
    active: true,
    details: "",
    locations: [],
    startDate: "",
    endDate: "",
    targetNumber: "",
    trainingType: "",
    fundedBy: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [stateOptions, setStateOptions] = useState([]); // For location select
  const [trainingTypeOptions] = useState([
    { value: "STT", label: "STT" },
    { value: "RPL", label: "RPL" },
    { value: "Other", label: "Other" },
  ]);

  const [fundedByOptions] = useState([
    { value: "State Government", label: "State Government" },
    { value: "Central Government", label: "Central Government" },
    { value: "Industry", label: "Industry" },
    { value: "Self-Funded", label: "Self-Funded" },
    { value: "NGO", label: "NGO" },
    { value: "Paid", label: "Paid" },
  ]);

  useEffect(() => {
    localStorage.setItem("sb|sidebar-toggle", isSidebarOpen);
  }, [isSidebarOpen]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    localStorage.setItem("sb|sidebar-toggle", !isSidebarOpen);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch states for locations
        const statesResponse = await getState();
        const formattedStates = statesResponse.data.map((state) => ({
          value: state.name,
          label: state.name,
        }));
        setStateOptions(formattedStates);
        console.log("id " + id + ", " + mode);

        if (mode === "edit" && id) {
          const response = await getProjectById(id);
          const projectData = response.data;

          setFormData({
            ...projectData,
            startDate: projectData.startDate
              ? new Date(projectData.startDate).toISOString().split("T")[0]
              : "",
            endDate: projectData.endDate
              ? new Date(projectData.endDate).toISOString().split("T")[0]
              : "",
            targetNumber:
              projectData.targetNumber !== null
                ? String(projectData.targetNumber)
                : "",
            locations: Array.isArray(projectData.locations)
              ? projectData.locations
              : projectData.locations
              ? projectData.locations.split(",").map((s) => s.trim())
              : [],
          });
        }
      } catch (error) {
        if (setToast) {
          setToast("Failed to load project data", "error");
        }
        navigate("/project");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, mode, navigate, setToast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, selectedOptions) => {
    const values = selectedOptions
      ? selectedOptions.map((option) => option.value).join(",")
      : "";
    setFormData((prev) => ({ ...prev, [name]: values }));
  };

  const handleSingleSelectChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Project name is required";
    if (!formData.details.trim())
      newErrors.details = "Project details are required";
    if (formData.locations.length === 0)
      newErrors.locations = "At least one location is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    if (!formData.endDate) newErrors.endDate = "End date is required";
    if (!formData.targetNumber)
      newErrors.targetNumber = "Target number is required";
    if (!formData.trainingType)
      newErrors.trainingType = "Training type is required";
    if (!formData.fundedBy) newErrors.fundedBy = "Funding source is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        targetNumber: formData.targetNumber
          ? parseInt(formData.targetNumber)
          : null,
        locations: Array.isArray(formData.locations)
          ? formData.locations.join(",")
          : formData.locations,  
      };

      if (mode === "add") {
        await createProject(dataToSubmit);
        if (setToast) setToast("Project created successfully", "success");
      } else {
         console.log("Data to Submit (JSON):", JSON.stringify(dataToSubmit, null, 2)); 
        // Pretty print with 2 spaces indentation
        await updateProject(dataToSubmit);
        if (setToast) setToast("Project updated successfully", "success");
      }
      navigate("/project");
    } catch (error) {
      if (setToast) {
        setToast(
          `Failed to ${mode === "add" ? "create" : "update"} project`,
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div
      className={`sb-nav-fixed ${isSidebarOpen ? "sb-sidenav-toggled" : ""}`}
    >
      <TopNav toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
      <div id="layoutSidenav">
        <SideNav isSidebarOpen={isSidebarOpen} />
        <div id="layoutSidenav_content">
          <main>
            <div className="container-fluid px-4">
              <h4 className="mt-4">
                {mode === "add" ? "Add New Project" : "Edit Project"}
              </h4>
              <div className="card mb-4">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Project Name<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control form-control-sm ${
                            errors.name ? "is-invalid" : ""
                          }`}
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                        />
                        {errors.name && (
                          <div className="invalid-feedback">{errors.name}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Status</label>
                        <div className="form-check form-switch mt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={() =>
                              setFormData((p) => ({ ...p, active: !p.active }))
                            }
                          />
                          <label className="form-check-label">
                            {formData.active ? "Active" : "Inactive"}
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-12">
                        <label className="form-label">
                          Project Details<span className="text-danger">*</span>
                        </label>
                        <textarea
                          className={`form-control form-control-sm ${
                            errors.details ? "is-invalid" : ""
                          }`}
                          name="details"
                          value={formData.details}
                          onChange={handleChange}
                          rows={3}
                        />
                        {errors.details && (
                          <div className="invalid-feedback">
                            {errors.details}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          State<span className="text-danger">*</span>
                        </label>
                        <Select
                          isMulti
                          options={stateOptions}
                          value={stateOptions.filter((option) =>
                            formData.locations.includes(option.value)
                          )}
                          onChange={(selected) =>
                            handleSelectChange("locations", selected)
                          }
                          className={`${
                            errors.locations ? "is-invalid-select" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select locations..."
                          closeMenuOnSelect={false}
                        />
                        {errors.locations && (
                          <div className="invalid-feedback d-block">
                            {errors.locations}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <div className="row">
                          <div className="col-md-6">
                            <label className="form-label">
                              Start Date<span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className={`form-control form-control-sm ${
                                errors.startDate ? "is-invalid" : ""
                              }`}
                              name="startDate"
                              value={formData.startDate}
                              onChange={handleChange}
                            />
                            {errors.startDate && (
                              <div className="invalid-feedback">
                                {errors.startDate}
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">
                              End Date<span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              className={`form-control form-control-sm ${
                                errors.endDate ? "is-invalid" : ""
                              }`}
                              name="endDate"
                              value={formData.endDate}
                              onChange={handleChange}
                            />
                            {errors.endDate && (
                              <div className="invalid-feedback">
                                {errors.endDate}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          Target Number<span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className={`form-control form-control-sm ${
                            errors.targetNumber ? "is-invalid" : ""
                          }`}
                          name="targetNumber"
                          value={formData.targetNumber}
                          onChange={handleChange}
                        />
                        {errors.targetNumber && (
                          <div className="invalid-feedback">
                            {errors.targetNumber}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Training Type<span className="text-danger">*</span>
                        </label>
                        <Select
                          options={trainingTypeOptions}
                          value={trainingTypeOptions.find(
                            (option) => option.value === formData.trainingType
                          )}
                          onChange={(selected) =>
                            handleSingleSelectChange("trainingType", selected)
                          }
                          className={`${
                            errors.trainingType ? "is-invalid-select" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select training type..."
                        />
                        {errors.trainingType && (
                          <div className="invalid-feedback d-block">
                            {errors.trainingType}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Funded By<span className="text-danger">*</span>
                        </label>
                        <Select
                          options={fundedByOptions}
                          value={fundedByOptions.find(
                            (option) => option.value === formData.fundedBy
                          )}
                          onChange={(selected) =>
                            handleSingleSelectChange("fundedBy", selected)
                          }
                          className={`${
                            errors.fundedBy ? "is-invalid-select" : ""
                          }`}
                          classNamePrefix="select"
                          placeholder="Select funding source..."
                        />
                        {errors.fundedBy && (
                          <div className="invalid-feedback d-block">
                            {errors.fundedBy}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate("/project")}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary btn-sm">
                        <FaSave className="me-1" />
                        {mode === "add" ? "Create Project" : "Update Project"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
